import { LineExtensionPayload } from '@mangovoice/types'

export enum WebSocketEventType {
  MessageReceived = 'MessageReceived',
  ThreadCreated = 'ThreadCreated',
  ThreadUpdated = 'ThreadUpdated',
  GroupUpdated = 'GroupUpdated',
  Ping = 'PING',
  CallRinging = 'LINE_EXTENSION_RINGING',
  CallAnswered = 'LINE_EXTENSION_ANSWERED',
  CallHangup = 'LINE_EXTENSION_HANGUP',
}

export type WebSocketEventPayload =
  | ThreadUpdatedPayload
  | MessageReceivedPayload
  | MessageGroupPayload
  | ExtensionJoinedPayload
  | null
  | undefined

export type WebSocketCommandPayload = ExtensionJoinedPayload | null | undefined

export enum WebSocketCommandType {
  ExtensionJoined = 'ExtensionJoined',
  ExtensionLeft = 'ExtensionLeft',
  Pong = 'PONG',
}

type WebSocketEventPayloadMap = {
  [WebSocketEventType.MessageReceived]: MessageReceivedPayload
  [WebSocketEventType.ThreadCreated]: ThreadUpdatedPayload
  [WebSocketEventType.ThreadUpdated]: ThreadUpdatedPayload
  [WebSocketEventType.GroupUpdated]: MessageGroupPayload
  [WebSocketEventType.Ping]: undefined
  [WebSocketEventType.CallRinging]: LineExtensionPayload
  [WebSocketEventType.CallAnswered]: LineExtensionPayload
  [WebSocketEventType.CallHangup]: LineExtensionPayload
}

export type WebSocketEventPayloadFromEventType<T extends WebSocketEventType> =
  WebSocketEventPayloadMap[T]

type WebSocketCommandPayloadMap = {
  [WebSocketCommandType.ExtensionJoined]: ExtensionJoinedPayload
  [WebSocketCommandType.ExtensionLeft]: ExtensionJoinedPayload
  [WebSocketCommandType.Pong]: undefined
}

export type WebSocketCommandPayloadFromEventType<
  T extends WebSocketCommandType,
> = WebSocketCommandPayloadMap[T]

export type WebSocketEventBase<TEventType extends WebSocketEventType> = {
  eventType: TEventType
  eventTimestamp?: string
  /** Monotonic id stamped by chat-rtm on broadcast events; reconnects pass
   * the highest id seen as ?lastEventId= so the server replays missed
   * events (5-minute / 100-event buffer per pbx). */
  eventId?: number
}

export type WebSocketEvent<TEventType extends WebSocketEventType> =
  WebSocketEventBase<TEventType> & {
    payload: WebSocketEventPayloadFromEventType<TEventType>
  }

export type WebSocketCommandBase<TCommandType extends WebSocketCommandType> = {
  eventType: TCommandType
  eventTimestamp?: string
}

/**Ensure instances created of ChatRtmCommand are cast to their literal values
 * Example: const pongCommand: { eventType: ChatRtmCommandType.Pong } as ChatRtmCommand<ChatRtmCommandType.Pong>
 * This is due to conditional type inference not working as expected in Typescript - forward declaration of the generic type does not resolve conditional undefined types.
 * This is a non issue for types that have a payload value - the payload type is inferred correctly since it won't be undefined.
 */
export type WebSocketCommand<TCommandType extends WebSocketCommandType> =
  WebSocketCommandBase<TCommandType> & {
    payload: WebSocketCommandPayloadFromEventType<TCommandType>
  }

export interface ExtensionJoinedPayload {
  threadId: number
  name: string
  eventTimestamp: string
}

export interface MessageGroupPayload {
  typeName: string | 'TEAM_MESSAGING_GROUP'
  id: number
  name: string
  pbxId: number
  createdAt: string
  updatedAt: string
  deletedAt?: string
  type: number
}

export interface ThreadUpdatedPayload {
  typeName: string | 'GROUP_TEXT_THREAD'
  id: number
  groupId: number
  name: string
  type: number
  externalDID: string
  updatedAt: string
  createdAt: string
  group?: MessageGroupPayload
}

export interface MessageReceivedPayload {
  typeName: string | 'TEXT_MESSAGE'
  mediaUrl?: string
  isImageFile: boolean
  fileSize?: number
  text: string
  id: number
  uuid: string
  threadId: number
  type: number
  fileBytes?: ArrayBuffer
  fileMimeType?: string
  fileKey?: string
  fileName?: string
  updatedAt: string
  createdAt: string
  group?: MessageGroupPayload
}
